"use client"

import { Star, StarSolid } from "@medusajs/icons"
import type { StoreProductReviewSummary } from "../../types"
import styles from "./review-summary.module.css"

type ReviewSummaryProps = {
  aggregate: StoreProductReviewSummary | null
  fallbackRating?: number | null
  fallbackCountText?: string | null
  reviewSectionId: string
  interactive: boolean
}

const clampRating = (value: number) => Math.min(5, Math.max(0, value))

export default function ReviewSummary({
  aggregate,
  fallbackRating,
  fallbackCountText,
  reviewSectionId,
  interactive,
}: ReviewSummaryProps) {
  const rating = typeof fallbackRating === "number"
    ? fallbackRating
    : aggregate?.average_rating ?? null
  const countText = fallbackCountText?.trim() || (aggregate ? `${aggregate.total} yorum` : null)

  if (rating === null && !countText) return null

  const safeRating = rating === null ? 0 : clampRating(rating)
  const roundedStars = Math.round(safeRating)

  const content = (
    <>
      <span className={styles.reviewStars} aria-hidden="true">
        {Array.from({ length: 5 }, (_, index) => {
          const Icon = index < roundedStars ? StarSolid : Star
          return <Icon key={index} className={styles.reviewStar} />
        })}
      </span>
      {rating !== null ? <span className={styles.reviewRating}>{safeRating.toFixed(1)}</span> : null}
      {rating !== null && countText ? <span className={styles.reviewSeparator} aria-hidden="true">|</span> : null}
      {countText ? <span className={styles.reviewCount}>{countText}</span> : null}
    </>
  )

  if (!interactive) {
    return <div className={styles.reviewSummary}>{content}</div>
  }

  const handleClick = () => {
    const target = document.getElementById(reviewSectionId)
    if (!target) return
    target.scrollIntoView({ behavior: "smooth", block: "start" })
    target.focus({ preventScroll: true })
  }

  return (
    <button
      type="button"
      className={`${styles.reviewSummary} ${styles.reviewSummaryInteractive}`}
      onClick={handleClick}
      aria-label={`${countText ?? "Yorumlar"}a git`}
    >
      {content}
    </button>
  )
}
